import { useEffect, useRef, useState } from 'react';
import {
  DASHBOARD_BUTTONS,
  DASHBOARD_CATEGORIES,
  DEFAULT_DASHBOARD_VIEW,
  SETTINGS_ROLES,
  type DashboardCategory,
  type DashboardView,
} from '@ehr/shared';
import { api } from '../lib/api.js';
import { DASHBOARD_VIEW_EVENT } from '../lib/dashboardView.js';
import { IconX, ROLE_LABEL, Shell, msg, uname, type Team, type TeamUser } from './usersShared.js';
import './usersModal.css';

/**
 * Dashboard View (EHR_08-14-V38 dvRenderModal, ehr#60): which contact category tabs show
 * (Clients / Referral Sources / Staff), their order and labels, and the Referrals /
 * Export / Notifications buttons. Save sets the practice default and pushes it to the
 * users and roles picked at the top. Opened from a user's row, that user is pre-picked.
 */
export const CATEGORY_LABEL: Record<DashboardCategory, string> = {
  client: 'Clients',
  referral_source: 'Referral Sources',
  staff: 'Staff',
};
const BUTTON_LABEL: Record<(typeof DASHBOARD_BUTTONS)[number], string> = {
  referral_bucket: 'Referrals bucket',
  export: 'Export',
  notifications: 'Notifications',
};

const GRIP = (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden>
    <circle cx="4" cy="2.5" r="1" />
    <circle cx="8" cy="2.5" r="1" />
    <circle cx="4" cy="6" r="1" />
    <circle cx="8" cy="6" r="1" />
    <circle cx="4" cy="9.5" r="1" />
    <circle cx="8" cy="9.5" r="1" />
  </svg>
);
const PENCIL = (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

/** Fill in any missing categories so the list always has all three. */
function normalize(v: DashboardView | null): DashboardView {
  const base = v ?? DEFAULT_DASHBOARD_VIEW;
  const order = [
    ...base.order,
    ...DASHBOARD_CATEGORIES.filter((c) => !base.order.includes(c)),
  ] as DashboardCategory[];
  return {
    order,
    visible: { ...base.visible },
    labels: { ...base.labels },
    buttons: { ...base.buttons },
  };
}

export function DashboardViewWindow({
  team,
  users,
  initial,
  preselectUserId,
  onBack,
  onSaved,
}: {
  team: Team;
  users: TeamUser[];
  initial: DashboardView | null;
  preselectUserId?: string;
  onBack: () => void;
  onSaved: () => void;
}) {
  const [view, setView] = useState<DashboardView>(() => normalize(initial));
  const [pushUsers, setPushUsers] = useState<Set<string>>(
    () => new Set(preselectUserId ? [preselectUserId] : []),
  );
  const [pushRoles, setPushRoles] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<DashboardCategory | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const drag = useRef<DashboardCategory | null>(null);

  useEffect(() => {
    api
      .get<Record<string, number>>('/contacts/counts')
      .then((c) => setCounts(c ?? {}))
      .catch(() => undefined);
  }, []);

  const shownCount = view.order.filter((c) => view.visible[c] !== false).length;
  const label = (c: DashboardCategory) => view.labels[c] || CATEGORY_LABEL[c];
  const setVis = (c: DashboardCategory, v: boolean) =>
    setView({ ...view, visible: { ...view.visible, [c]: v } });
  const rename = (c: DashboardCategory, v: string | null) => {
    setEditing(null);
    if (v === null) return;
    const t = v.trim();
    const labels = { ...view.labels };
    if (!t || t === CATEGORY_LABEL[c]) delete labels[c];
    else labels[c] = t.slice(0, 40);
    setView({ ...view, labels });
  };
  const drop = (target: DashboardCategory) => {
    const from = drag.current;
    drag.current = null;
    if (!from || from === target) return;
    const order = view.order.filter((c) => c !== from);
    order.splice(order.indexOf(target), 0, from);
    setView({ ...view, order });
  };

  const roleOpts = [
    ...SETTINGS_ROLES.map((r) => ({ key: `sys:${r}`, name: ROLE_LABEL[r] })),
    ...team.roles.map((r) => ({ key: `cr:${r.id}`, name: r.name })),
  ];
  const active = users.filter((u) => u.active);
  const ql = q.trim().toLowerCase();
  const roleHits = ql ? roleOpts.filter((r) => r.name.toLowerCase().includes(ql)) : [];
  const userHits = ql
    ? active.filter((u) => [uname(u), u.email].some((s) => s.toLowerCase().includes(ql)))
    : [];
  const flip = (set: Set<string>, id: string, put: (s: Set<string>) => void) => {
    const n = new Set(set);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    put(n);
  };

  const save = () => {
    setSaving(true);
    setErr(null);
    api
      .put<{ pushed: number }>('/settings/team/dashboard-view', {
        view,
        pushUserIds: [...pushUsers],
        pushRoles: [...pushRoles],
      })
      .then(() => {
        window.dispatchEvent(new Event(DASHBOARD_VIEW_EVENT));
        onSaved();
        onBack();
      })
      .catch((e: unknown) => setErr(msg(e)))
      .finally(() => setSaving(false));
  };

  return (
    <Shell
      title="Dashboard View"
      sub="Choose which lists appear on the dashboard, plus their order and labels. Admin-only."
      onClose={onBack}
      className="dv-modal"
      foot={
        <>
          <button
            className="btn dv-reset"
            onClick={() => setView(normalize(DEFAULT_DASHBOARD_VIEW))}
          >
            Reset to default order
          </button>
          <button className="btn" onClick={onBack}>
            Cancel
          </button>
          <button className="btn primary" disabled={saving} onClick={save}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {err && <div className="config-err">{err}</div>}
      <div className="dv-push">
        <div className="dv-head">Push to users / roles</div>
        <div className="dv-hint">
          Select users or roles to push this category config to on Save. Their dashboard updates
          immediately.
        </div>
        <label className="dv-push-search">
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            placeholder="Search users or roles…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        {(pushRoles.size > 0 || pushUsers.size > 0) && (
          <div className="dv-chips">
            {[...pushRoles].map((k) => (
              <span className="dv-chip" key={k}>
                {roleOpts.find((r) => r.key === k)?.name ?? k}
                <small>role</small>
                <button aria-label="Remove" onClick={() => flip(pushRoles, k, setPushRoles)}>
                  {IconX}
                </button>
              </span>
            ))}
            {[...pushUsers].map((id) => {
              const u = users.find((x) => x.id === id);
              return (
                <span className="dv-chip" key={id}>
                  {u ? uname(u) : id}
                  <button aria-label="Remove" onClick={() => flip(pushUsers, id, setPushUsers)}>
                    {IconX}
                  </button>
                </span>
              );
            })}
          </div>
        )}
        {ql ? (
          <div className="dv-push-list">
            {roleHits.map((r) => (
              <label className="dv-push-row" key={r.key}>
                <input
                  type="checkbox"
                  checked={pushRoles.has(r.key)}
                  onChange={() => flip(pushRoles, r.key, setPushRoles)}
                />
                <span>{r.name}</span>
                <small>Role</small>
              </label>
            ))}
            {userHits.map((u) => (
              <label className="dv-push-row" key={u.id}>
                <input
                  type="checkbox"
                  checked={pushUsers.has(u.id)}
                  onChange={() => flip(pushUsers, u.id, setPushUsers)}
                />
                <span>{uname(u)}</span>
                <small>{u.email}</small>
              </label>
            ))}
            {roleHits.length + userHits.length === 0 && (
              <div className="dv-hint dv-italic">No users or roles match “{q}”.</div>
            )}
          </div>
        ) : (
          <div className="dv-hint dv-italic">
            Type a name or role to find users and roles to push to.
          </div>
        )}
      </div>
      <div className="dv-section">
        <div className="dv-head">Categories</div>
        <div className="dv-hint">
          Drag to reorder. Toggle visibility. Click ✎ to rename. All changes apply when you click
          Save.
        </div>
        {view.order.map((c) => {
          const on = view.visible[c] !== false;
          const last = on && shownCount === 1;
          return (
            <div
              key={c}
              className={`dv-row${last ? ' dv-disabled' : ''}`}
              draggable
              onDragStart={() => (drag.current = c)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                drop(c);
              }}
              title={last ? 'At least one category must remain visible' : undefined}
            >
              <span className="dv-grip">{GRIP}</span>
              <input
                type="checkbox"
                checked={on}
                disabled={last}
                aria-label={`Show ${label(c)}`}
                onChange={(e) => setVis(c, e.target.checked)}
              />
              {editing === c ? (
                <input
                  className="dv-edit-input"
                  autoFocus
                  defaultValue={label(c)}
                  maxLength={40}
                  onBlur={(e) => rename(c, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') rename(c, e.currentTarget.value);
                    if (e.key === 'Escape') {
                      e.stopPropagation();
                      rename(c, null);
                    }
                  }}
                />
              ) : (
                <span className="dv-label-text">
                  {label(c)}
                  {view.labels[c] && (
                    <span className="dv-custom-dot" title={`Renamed from ${CATEGORY_LABEL[c]}`}>
                      ●
                    </span>
                  )}
                </span>
              )}
              <span className="dv-count">{counts[c] ?? 0}</span>
              <button className="dv-icon-btn" title="Edit label" onClick={() => setEditing(c)}>
                {PENCIL}
              </button>
            </div>
          );
        })}
        <div className="dv-divider" />
        <div className="dv-head">System buttons</div>
        <div className="dv-hint">Show or hide these toolbar buttons.</div>
        {DASHBOARD_BUTTONS.map((b) => (
          <label className="dv-row dv-row-plain" key={b}>
            <input
              type="checkbox"
              checked={view.buttons[b] !== false}
              onChange={(e) =>
                setView({ ...view, buttons: { ...view.buttons, [b]: e.target.checked } })
              }
            />
            <span className="dv-label-text">{BUTTON_LABEL[b]}</span>
          </label>
        ))}
      </div>
    </Shell>
  );
}
